<template>
  <app-modal
    :model-value="isShown"
    :title="copy.title"
    :close-on-confirm="false"
    @update:model-value="dismissed = true"
  >
    <!-- AppModal does not pass attributes on, so the markers live in the body. -->
    <p class="text-sm leading-6 text-theme-text-secondary" data-test="checkout-required-dialog" :data-mode="mode">
      {{ copy.body }}
    </p>
    <template #footer>
      <dialog-actions
        :primary-label="copy.cta"
        :secondary-label="t('general_button_close')"
        @primary="proceed"
        @secondary="dismissed = true"
      />
    </template>
  </app-modal>
</template>

<script setup>
  /**
   * Nudge for signed-in accounts without an active subscription. Never a wall: it
   * closes (button, x, Escape, backdrop) and stays away for the rest of the visit.
   *
   *   - `payment`   a missed payment blocked the account: the billing page.
   *   - `subscribe` anything else (never subscribed, left the trial checkout, expired
   *                 trial, cancelled): the plans page, where trial or plan is chosen and
   *                 a promotion code can be entered. Never straight into a Stripe Checkout.
   *
   * Sales-managed, API-only and super-user accounts never see it, nor do the payment
   * routes themselves. UX only: entitlements stay the security boundary.
   */
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  import DialogActions from '@/components/billing/DialogActions.vue';
  import AppModal from '@/components/ui/overlays/AppModal.vue';
  import { SALES_MANAGED_BILLING_STATES } from '@/composables/usePlanContext';
  import { useAuthStore } from '@/stores/useAuthStore';

  defineOptions({ name: 'CheckoutRequiredDialog' });

  // Routes where the user can (or is just about to) pay; nudging there would loop.
  const PAYMENT_ROUTE_NAMES = new Set([
    'subscriptions',
    'subscriptions-upgrade-flex',
    'subscriptions-upgrade-tools',
    'subscriptions-upgrade-basic',
    'subscription-welcome',
    'stripe.subscribed-callback',
    'my-subscription',
    'subscription.invoices',
  ]);
  // Mirrors the auth guard: these hard-block the account (grace_period keeps access).
  const PAYMENT_BLOCKED_STATES = ['past_due', 'unpaid'];

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const dismissed = ref(false);

  const isShown = computed(
    () =>
      !dismissed.value &&
      authStore.isAuthenticated &&
      Boolean(authStore.id) &&
      !authStore.isSuperUser &&
      !authStore.isApiOnly &&
      !authStore.hasValidSubscription &&
      !SALES_MANAGED_BILLING_STATES.includes(authStore.billingAccountState) &&
      route.meta?.requiresAuth !== false &&
      !route.meta?.formScreen &&
      !PAYMENT_ROUTE_NAMES.has(route.name)
  );

  const mode = computed(() => {
    if (PAYMENT_BLOCKED_STATES.includes(authStore.billingAccountState)) return 'payment';
    return 'subscribe';
  });

  const copy = computed(() => {
    if (mode.value === 'payment') {
      const state = authStore.billingAccountState;
      return {
        title: t(`billing.dunning.${state}.title`),
        body: t(`billing.dunning.${state}.message`),
        cta: t('billing.dunning.fix_action'),
      };
    }
    const key = `billing.checkout_required.${mode.value}`;
    return { title: t(`${key}.title`), body: t(`${key}.body`), cta: t(`${key}.cta`) };
  });

  const proceed = () => {
    dismissed.value = true;
    if (mode.value === 'payment') {
      return router.push({ name: 'subscription.invoices', query: { billing: authStore.billingAccountState } });
    }
    return router.push({ name: 'subscriptions' });
  };
</script>
